import React from "react";

/* ============================================================
   Spooky Scales — cottage clip art library
   Palette limited to logo tones:
     sage      #A9BE9D
     lightSage #C7D5B8
     forest    #294332
     moss      #4A5D23
     cream     #F8F4EA
     gold      #CFB53B  (used sparingly)
   ============================================================ */

const S = {
  sage: "#A9D93E",       // canopy lime pop
  lightSage: "#C6E86E",
  forest: "#1B3924",     // deep jungle
  moss: "#4D6E42",
  cream: "#F5EFD8",
  gold: "#D6B26A",
  purple: "#5B2F8B",
  lavender: "#C48CE8",
  magenta: "#C48CE8",
  midnight: "#0F1F14",
};

/* ---------------- Reptiles ---------------- */

export const Snake = ({ className = "", size = 90 }) => (
  <svg viewBox="0 0 120 60" width={size} height={size} className={className} aria-hidden="true">
    <path
      d="M6 40 Q20 10, 40 30 Q60 50, 80 20 Q100 -5, 114 24"
      fill="none"
      stroke={S.sage}
      strokeWidth="10"
      strokeLinecap="round"
    />
    <path
      d="M6 40 Q20 10, 40 30 Q60 50, 80 20 Q100 -5, 114 24"
      fill="none"
      stroke={S.moss}
      strokeWidth="1.2"
      strokeDasharray="2 8"
      strokeLinecap="round"
    />
    <circle cx="114" cy="24" r="2" fill={S.forest} />
    <path d="M115 25 L120 22 M115 26 L120 28" stroke={S.forest} strokeWidth="1.2" />
  </svg>
);

export const CurledSnake = ({ className = "", size = 90 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} className={className} aria-hidden="true">
    <path
      d="M50 90 Q20 90, 20 60 Q20 30, 50 30 Q75 30, 75 55 Q75 72, 55 72 Q42 72, 42 60 Q42 52, 52 52"
      fill="none"
      stroke={S.sage}
      strokeWidth="8"
      strokeLinecap="round"
    />
    <path
      d="M50 90 Q20 90, 20 60 Q20 30, 50 30 Q75 30, 75 55 Q75 72, 55 72 Q42 72, 42 60 Q42 52, 52 52"
      fill="none"
      stroke={S.moss}
      strokeWidth="1"
      strokeDasharray="1 5"
    />
    <circle cx="52" cy="52" r="1.5" fill={S.forest} />
    <path d="M53 53 L57 51 M53 53.5 L57 55" stroke={S.forest} strokeWidth="1" />
  </svg>
);

export const SnakeS = ({ className = "", size = 80 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} className={className} aria-hidden="true">
    <path
      d="M15 85 Q35 55, 15 40 Q0 20, 30 15 Q60 15, 60 40 Q60 60, 80 60 Q98 60, 90 85"
      fill="none"
      stroke={S.sage}
      strokeWidth="8"
      strokeLinecap="round"
    />
    <path
      d="M15 85 Q35 55, 15 40 Q0 20, 30 15 Q60 15, 60 40 Q60 60, 80 60 Q98 60, 90 85"
      fill="none"
      stroke={S.moss}
      strokeWidth="1"
      strokeDasharray="1 5"
    />
    <circle cx="30" cy="15" r="1.8" fill={S.forest} />
  </svg>
);

export const Gecko = ({ className = "", size = 90 }) => (
  <svg viewBox="0 0 120 80" width={size} height={size} className={className} aria-hidden="true">
    <path
      d="M20 45 Q10 40, 12 32 Q16 22, 30 26 Q42 22, 55 30 Q70 22, 85 30 Q100 34, 106 42 Q108 50, 100 52 Q92 54, 84 48 Q70 60, 55 52 Q42 60, 30 52 Q22 54, 20 45 Z"
      fill={S.sage}
      stroke={S.forest}
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path d="M32 50 L28 62 M40 52 L42 66 M74 52 L72 66 M84 50 L88 62" stroke={S.forest} strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="28" cy="63" r="2.2" fill={S.sage} stroke={S.forest} strokeWidth="1" />
    <circle cx="42" cy="67" r="2.2" fill={S.sage} stroke={S.forest} strokeWidth="1" />
    <circle cx="72" cy="67" r="2.2" fill={S.sage} stroke={S.forest} strokeWidth="1" />
    <circle cx="88" cy="63" r="2.2" fill={S.sage} stroke={S.forest} strokeWidth="1" />
    <path d="M20 45 Q6 55, 4 68" fill="none" stroke={S.sage} strokeWidth="6" strokeLinecap="round" />
    <path d="M20 45 Q6 55, 4 68" fill="none" stroke={S.forest} strokeWidth="1" />
    <circle cx="102" cy="40" r="2.4" fill={S.forest} />
    <circle cx="102.7" cy="39.3" r="0.8" fill={S.cream} />
    <circle cx="55" cy="38" r="1.6" fill={S.moss} opacity="0.7" />
    <circle cx="70" cy="42" r="1.4" fill={S.moss} opacity="0.7" />
    <circle cx="40" cy="40" r="1.4" fill={S.moss} opacity="0.7" />
  </svg>
);

export const BeardedDragon = ({ className = "", size = 100 }) => (
  <svg viewBox="0 0 130 80" width={size} height={size} className={className} aria-hidden="true">
    <path d="M115 42 Q126 46, 125 60" fill="none" stroke={S.sage} strokeWidth="6" strokeLinecap="round" opacity="0.85" />
    <ellipse cx="70" cy="44" rx="46" ry="16" fill={S.lightSage} stroke={S.forest} strokeWidth="1.3" />
    <path d="M22 40 Q10 38, 6 46 Q10 54, 22 52 Q30 50, 30 46 Q30 42, 22 40 Z" fill={S.lightSage} stroke={S.forest} strokeWidth="1.3" />
    <path d="M12 52 L10 58 M18 54 L18 60 M24 54 L26 60" stroke={S.forest} strokeWidth="1.2" strokeLinecap="round" />
    <path d="M40 32 L38 26 M50 30 L48 24 M60 29 L58 23 M70 29 L68 23 M80 30 L78 24 M90 32 L88 26 M100 34 L98 28" stroke={S.forest} strokeWidth="1.2" strokeLinecap="round" />
    <path d="M40 58 L38 68 L42 68 M48 58 L46 66 L50 66 M90 58 L88 68 L92 68 M100 58 L98 66 L102 66" stroke={S.forest} strokeWidth="1.3" fill={S.lightSage} strokeLinejoin="round" />
    <circle cx="14" cy="45" r="2.4" fill={S.forest} />
    <circle cx="14.7" cy="44.3" r="0.8" fill={S.cream} />
    <path d="M28 50 Q70 60, 110 50" fill="none" stroke={S.forest} strokeWidth="0.8" opacity="0.4" />
  </svg>
);

export const Turtle = ({ className = "", size = 90 }) => (
  <svg viewBox="0 0 120 80" width={size} height={size} className={className} aria-hidden="true">
    <ellipse cx="60" cy="42" rx="40" ry="26" fill={S.moss} stroke={S.forest} strokeWidth="1.4" />
    <path d="M60 16 L60 68" stroke={S.forest} strokeWidth="1" opacity="0.4" />
    <path d="M40 22 Q60 44, 80 22" fill="none" stroke={S.forest} strokeWidth="1" opacity="0.4" />
    <path d="M40 62 Q60 40, 80 62" fill="none" stroke={S.forest} strokeWidth="1" opacity="0.4" />
    <path d="M32 42 L88 42" stroke={S.forest} strokeWidth="1" opacity="0.4" />
    <path d="M45 30 L52 26 L59 30 L59 38 L52 42 L45 38 Z" fill={S.sage} opacity="0.9" stroke={S.forest} strokeWidth="0.8" />
    <path d="M61 30 L68 26 L75 30 L75 38 L68 42 L61 38 Z" fill={S.lightSage} opacity="0.9" stroke={S.forest} strokeWidth="0.8" />
    <path d="M45 46 L52 42 L59 46 L59 54 L52 58 L45 54 Z" fill={S.lightSage} opacity="0.9" stroke={S.forest} strokeWidth="0.8" />
    <path d="M61 46 L68 42 L75 46 L75 54 L68 58 L61 54 Z" fill={S.sage} opacity="0.9" stroke={S.forest} strokeWidth="0.8" />
    <ellipse cx="105" cy="42" rx="10" ry="7" fill={S.lightSage} stroke={S.forest} strokeWidth="1.3" />
    <circle cx="108" cy="41" r="1.6" fill={S.forest} />
    <circle cx="108.4" cy="40.5" r="0.5" fill={S.cream} />
    <ellipse cx="28" cy="30" rx="6" ry="4" fill={S.lightSage} stroke={S.forest} strokeWidth="1.1" transform="rotate(-20 28 30)" />
    <ellipse cx="28" cy="56" rx="6" ry="4" fill={S.lightSage} stroke={S.forest} strokeWidth="1.1" transform="rotate(20 28 56)" />
    <ellipse cx="88" cy="60" rx="5" ry="4" fill={S.lightSage} stroke={S.forest} strokeWidth="1.1" transform="rotate(20 88 60)" />
    <ellipse cx="88" cy="24" rx="5" ry="4" fill={S.lightSage} stroke={S.forest} strokeWidth="1.1" transform="rotate(-20 88 24)" />
    <path d="M20 42 L10 46 L10 40 Z" fill={S.lightSage} stroke={S.forest} strokeWidth="1.1" />
  </svg>
);

export const Frog = ({ className = "", size = 60 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} className={className} aria-hidden="true">
    <ellipse cx="32" cy="40" rx="24" ry="16" fill={S.sage} stroke={S.forest} strokeWidth="1.4" />
    <circle cx="20" cy="24" r="8" fill={S.sage} stroke={S.forest} strokeWidth="1.4" />
    <circle cx="44" cy="24" r="8" fill={S.sage} stroke={S.forest} strokeWidth="1.4" />
    <circle cx="20" cy="24" r="3.2" fill={S.forest} />
    <circle cx="44" cy="24" r="3.2" fill={S.forest} />
    <circle cx="21" cy="23" r="1" fill={S.cream} />
    <circle cx="45" cy="23" r="1" fill={S.cream} />
    <path d="M24 44 Q32 50, 40 44" fill="none" stroke={S.forest} strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="15" cy="42" r="2" fill={S.moss} opacity="0.5" />
    <circle cx="48" cy="44" r="1.8" fill={S.moss} opacity="0.5" />
  </svg>
);

export const SnakeEgg = ({ className = "", size = 44 }) => (
  <svg viewBox="0 0 40 50" width={size} height={size} className={className} aria-hidden="true">
    <ellipse cx="20" cy="28" rx="14" ry="20" fill={S.cream} stroke={S.forest} strokeWidth="1.3" />
    <path d="M12 20 Q20 24, 28 20" fill="none" stroke={S.sage} strokeWidth="0.9" />
    <path d="M10 30 Q20 34, 30 30" fill="none" stroke={S.sage} strokeWidth="0.9" />
    <circle cx="15" cy="24" r="0.9" fill={S.moss} opacity="0.7" />
    <circle cx="25" cy="34" r="0.9" fill={S.moss} opacity="0.7" />
  </svg>
);

/* ---------------- Botanicals ---------------- */

export const Leaf = ({ className = "", size = 48 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} className={className} aria-hidden="true">
    <path d="M8 40 Q20 4, 56 12 Q52 48, 12 56 Z" fill={S.sage} stroke={S.forest} strokeWidth="1.3" />
    <path d="M12 52 Q30 30, 52 14" fill="none" stroke={S.forest} strokeWidth="1.1" opacity="0.55" />
    <path d="M20 44 L34 36 M26 50 L40 42 M18 36 L28 30" stroke={S.forest} strokeWidth="0.7" opacity="0.45" />
  </svg>
);

export const Vine = ({ className = "" }) => (
  <svg viewBox="0 0 400 80" className={className} aria-hidden="true" preserveAspectRatio="none">
    <path
      d="M0 40 Q50 10, 100 40 T200 40 T300 40 T400 40"
      fill="none"
      stroke={S.moss}
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <g fill={S.sage} stroke={S.forest} strokeWidth="1">
      <ellipse cx="60" cy="26" rx="9" ry="5" transform="rotate(-25 60 26)" />
      <ellipse cx="140" cy="54" rx="9" ry="5" transform="rotate(25 140 54)" />
      <ellipse cx="220" cy="26" rx="9" ry="5" transform="rotate(-25 220 26)" />
      <ellipse cx="300" cy="54" rx="9" ry="5" transform="rotate(25 300 54)" />
      <ellipse cx="360" cy="30" rx="8" ry="4" transform="rotate(-15 360 30)" />
    </g>
  </svg>
);

export const Fern = ({ className = "", size = 80 }) => (
  <svg viewBox="0 0 80 100" width={size} height={size} className={className} aria-hidden="true">
    <path d="M40 95 Q40 60, 40 20" stroke={S.moss} strokeWidth="2" fill="none" strokeLinecap="round" />
    <g fill={S.sage} stroke={S.forest} strokeWidth="1">
      <ellipse cx="30" cy="80" rx="12" ry="4" transform="rotate(-25 30 80)" />
      <ellipse cx="50" cy="80" rx="12" ry="4" transform="rotate(25 50 80)" />
      <ellipse cx="28" cy="65" rx="11" ry="3.5" transform="rotate(-25 28 65)" />
      <ellipse cx="52" cy="65" rx="11" ry="3.5" transform="rotate(25 52 65)" />
      <ellipse cx="26" cy="50" rx="9" ry="3" transform="rotate(-25 26 50)" />
      <ellipse cx="54" cy="50" rx="9" ry="3" transform="rotate(25 54 50)" />
      <ellipse cx="28" cy="36" rx="7" ry="2.5" transform="rotate(-25 28 36)" />
      <ellipse cx="52" cy="36" rx="7" ry="2.5" transform="rotate(25 52 36)" />
      <ellipse cx="32" cy="24" rx="5" ry="2" transform="rotate(-25 32 24)" />
      <ellipse cx="48" cy="24" rx="5" ry="2" transform="rotate(25 48 24)" />
    </g>
    <path d="M40 20 Q36 12, 40 8 Q44 12, 40 20" fill={S.moss} />
  </svg>
);

export const Berries = ({ className = "", size = 60 }) => (
  <svg viewBox="0 0 60 60" width={size} height={size} className={className} aria-hidden="true">
    <path d="M18 8 Q26 20, 30 30" stroke={S.moss} strokeWidth="1.4" fill="none" />
    <path d="M42 10 Q34 22, 32 32" stroke={S.moss} strokeWidth="1.4" fill="none" />
    <circle cx="22" cy="35" r="7" fill={S.purple} stroke={S.forest} strokeWidth="1.1" />
    <circle cx="34" cy="42" r="7" fill={S.purple} stroke={S.forest} strokeWidth="1.1" />
    <circle cx="16" cy="46" r="6" fill={S.purple} stroke={S.forest} strokeWidth="1.1" />
    <circle cx="30" cy="52" r="6" fill={S.purple} stroke={S.forest} strokeWidth="1.1" />
    <circle cx="22" cy="33" r="1.4" fill={S.cream} opacity="0.7" />
    <circle cx="34" cy="40" r="1.4" fill={S.cream} opacity="0.7" />
    <ellipse cx="18" cy="6" rx="6" ry="3" fill={S.sage} stroke={S.forest} strokeWidth="1" transform="rotate(-25 18 6)" />
    <ellipse cx="42" cy="8" rx="6" ry="3" fill={S.sage} stroke={S.forest} strokeWidth="1" transform="rotate(25 42 8)" />
  </svg>
);

export const Acorn = ({ className = "", size = 44 }) => (
  <svg viewBox="0 0 40 50" width={size} height={size} className={className} aria-hidden="true">
    <path d="M20 4 Q22 2, 24 4" stroke={S.moss} strokeWidth="1.4" fill="none" strokeLinecap="round" />
    <path d="M8 12 Q20 6, 32 12 Q30 20, 20 22 Q10 20, 8 12 Z" fill={S.moss} stroke={S.forest} strokeWidth="1.2" />
    <path d="M12 20 Q20 42, 28 20 Q28 38, 20 46 Q12 38, 12 20 Z" fill={S.sage} stroke={S.forest} strokeWidth="1.2" />
    <path d="M14 26 L26 26 M14 32 L26 32" stroke={S.forest} strokeWidth="0.6" opacity="0.35" />
  </svg>
);

export const Mushroom = ({ className = "", size = 60 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} className={className} aria-hidden="true">
    <path d="M8 32 C8 18, 24 8, 32 8 C40 8, 56 18, 56 32 Z" fill={S.moss} stroke={S.forest} strokeWidth="1.4" />
    <circle cx="20" cy="22" r="3.2" fill={S.cream} />
    <circle cx="34" cy="16" r="2.2" fill={S.cream} />
    <circle cx="44" cy="26" r="2.8" fill={S.cream} />
    <circle cx="28" cy="28" r="1.8" fill={S.cream} />
    <path d="M22 32 L22 54 Q22 60, 32 60 Q42 60, 42 54 L42 32 Z" fill={S.cream} stroke={S.forest} strokeWidth="1.4" />
    <path d="M28 34 L28 58" stroke={S.forest} strokeWidth="0.9" opacity="0.35" />
    <path d="M36 34 L36 58" stroke={S.forest} strokeWidth="0.9" opacity="0.35" />
  </svg>
);

export const Pumpkin = ({ className = "", size = 60 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} className={className} aria-hidden="true">
    <path d="M32 10 C34 6, 40 4, 42 8 L38 14" fill="none" stroke={S.moss} strokeWidth="2" strokeLinecap="round" />
    <ellipse cx="20" cy="38" rx="12" ry="18" fill={S.sage} stroke={S.forest} strokeWidth="1.4" />
    <ellipse cx="32" cy="38" rx="14" ry="20" fill={S.lightSage} stroke={S.forest} strokeWidth="1.4" />
    <ellipse cx="44" cy="38" rx="12" ry="18" fill={S.sage} stroke={S.forest} strokeWidth="1.4" />
    <path d="M32 20 L32 56" stroke={S.forest} strokeWidth="1" opacity="0.35" />
  </svg>
);

/* ---------------- Grimoire pieces ---------------- */

export const Moon = ({ className = "", size = 40 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} className={className} aria-hidden="true">
    <path
      d="M42 10 a24 24 0 1 0 0 44 a20 20 0 1 1 0 -44 Z"
      fill={S.cream}
      stroke={S.forest}
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

export const Star = ({ className = "", size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} aria-hidden="true">
    <path
      d="M12 2 L13.8 9 L21 10.5 L15.5 15 L17 22 L12 18 L7 22 L8.5 15 L3 10.5 L10.2 9 Z"
      fill={S.gold}
      stroke={S.forest}
      strokeWidth="0.7"
      strokeLinejoin="round"
    />
  </svg>
);

export const Sparkle = ({ className = "", size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} aria-hidden="true">
    <path d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z" fill={S.moss} />
  </svg>
);

export const Potion = ({ className = "", size = 60 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} className={className} aria-hidden="true">
    <rect x="26" y="6" width="12" height="10" rx="2" fill={S.cream} stroke={S.forest} strokeWidth="1.3" />
    <path d="M22 16 L42 16 L46 26 Q52 42, 32 58 Q12 42, 18 26 Z" fill={S.lavender} stroke={S.forest} strokeWidth="1.3" />
    <ellipse cx="28" cy="38" rx="3" ry="2" fill={S.cream} opacity="0.75" />
    <circle cx="36" cy="46" r="1.8" fill={S.cream} opacity="0.65" />
  </svg>
);

export const Spellbook = ({ className = "", size = 60 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} className={className} aria-hidden="true">
    <path d="M6 14 L30 12 L30 58 L6 60 Z" fill={S.moss} stroke={S.forest} strokeWidth="1.3" />
    <path d="M58 14 L34 12 L34 58 L58 60 Z" fill={S.moss} stroke={S.forest} strokeWidth="1.3" />
    <path d="M32 12 L32 60" stroke={S.forest} strokeWidth="1.3" />
    <path d="M14 26 L26 25 M14 32 L26 31 M14 38 L26 37" stroke={S.cream} strokeWidth="1" opacity="0.75" />
    <path d="M38 25 L50 26 M38 31 L50 32 M38 37 L50 38" stroke={S.cream} strokeWidth="1" opacity="0.75" />
    <circle cx="32" cy="34" r="4" fill={S.gold} stroke={S.forest} strokeWidth="1.1" />
    <path d="M32 30 L32 38 M28 34 L36 34" stroke={S.forest} strokeWidth="1" />
  </svg>
);

export const Lantern = ({ className = "", size = 44 }) => (
  <svg viewBox="0 0 40 60" width={size} height={size} className={className} aria-hidden="true">
    <path d="M20 4 L20 10" stroke={S.forest} strokeWidth="1.3" />
    <path d="M8 12 L32 12" stroke={S.forest} strokeWidth="1.3" />
    <path d="M10 14 L30 14 L28 46 L12 46 Z" fill={S.gold} stroke={S.forest} strokeWidth="1.3" opacity="0.9" />
    <rect x="12" y="46" width="16" height="6" fill={S.moss} stroke={S.forest} strokeWidth="1.3" />
    <circle cx="20" cy="28" r="4" fill={S.cream} />
  </svg>
);

export const Crystal = ({ className = "", size = 40 }) => (
  <svg viewBox="0 0 40 60" width={size} height={size} className={className} aria-hidden="true">
    <path d="M20 4 L32 24 L26 56 L14 56 L8 24 Z" fill={S.lavender} stroke={S.forest} strokeWidth="1.3" />
    <path d="M20 4 L20 56 M8 24 L32 24" stroke={S.forest} strokeWidth="0.8" opacity="0.45" />
  </svg>
);

export const Skull = ({ className = "", size = 40 }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} className={className} aria-hidden="true">
    <path d="M8 22 C8 12, 40 12, 40 22 L40 32 C40 36, 36 38, 32 38 L30 44 L18 44 L16 38 C12 38, 8 36, 8 32 Z" fill={S.cream} stroke={S.forest} strokeWidth="1.3" />
    <circle cx="18" cy="26" r="4" fill={S.forest} />
    <circle cx="30" cy="26" r="4" fill={S.forest} />
    <path d="M22 34 L26 34" stroke={S.forest} strokeWidth="1.1" />
  </svg>
);

export const CircleStamp = ({ text, className = "", size = 160 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} className={className} aria-hidden="true">
    <defs>
      <path id="cst" d="M100,100 m-72,0 a72,72 0 1,1 144,0 a72,72 0 1,1 -144,0" />
    </defs>
    <text fontFamily="Fraunces, serif" fontSize="14" fill={S.forest} letterSpacing="6">
      <textPath href="#cst">{text}</textPath>
    </text>
    <circle cx="100" cy="100" r="52" fill="none" stroke={S.forest} strokeWidth="1" strokeDasharray="2 6" />
  </svg>
);

/* ---------------- Haunted mansion cute-spooky ---------------- */

export const Bat = ({ className = "", size = 60 }) => (
  <svg viewBox="0 0 80 60" width={size} height={size} className={className} aria-hidden="true">
    {/* wings */}
    <path
      d="M40 30 Q20 10, 6 22 Q12 28, 10 38 Q22 32, 28 40 Q34 34, 40 40 Q46 34, 52 40 Q58 32, 70 38 Q68 28, 74 22 Q60 10, 40 30 Z"
      fill={S.forest}
      stroke={S.midnight}
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    {/* body */}
    <ellipse cx="40" cy="32" rx="6" ry="9" fill={S.midnight} stroke={S.forest} strokeWidth="1" />
    {/* ears */}
    <path d="M36 22 L34 14 L39 20 Z" fill={S.forest} />
    <path d="M44 22 L46 14 L41 20 Z" fill={S.forest} />
    {/* eyes */}
    <circle cx="37.5" cy="30" r="1.4" fill={S.magenta} />
    <circle cx="42.5" cy="30" r="1.4" fill={S.magenta} />
    {/* fangs */}
    <path d="M38 34 L38.5 37 L39 34 Z M41 34 L41.5 37 L42 34 Z" fill={S.cream} />
  </svg>
);

export const Rose = ({ className = "", size = 60 }) => (
  // Daisy — white petals, sunny yellow center
  <svg viewBox="0 0 60 80" width={size} height={size} className={className} aria-hidden="true">
    {/* stem */}
    <path d="M30 30 Q30 55, 30 78" stroke={S.moss} strokeWidth="2" fill="none" strokeLinecap="round" />
    {/* leaves */}
    <ellipse cx="20" cy="50" rx="10" ry="4" fill={S.moss} stroke={S.forest} strokeWidth="1" transform="rotate(-30 20 50)" />
    <ellipse cx="40" cy="62" rx="10" ry="4" fill={S.moss} stroke={S.forest} strokeWidth="1" transform="rotate(30 40 62)" />
    {/* daisy petals — 8 evenly spaced */}
    <g stroke={S.forest} strokeWidth="1" fill={S.cream}>
      <ellipse cx="30" cy="8" rx="4" ry="9" />
      <ellipse cx="30" cy="32" rx="4" ry="9" />
      <ellipse cx="18" cy="20" rx="9" ry="4" />
      <ellipse cx="42" cy="20" rx="9" ry="4" />
      <ellipse cx="22" cy="12" rx="4" ry="8" transform="rotate(-45 22 12)" />
      <ellipse cx="38" cy="12" rx="4" ry="8" transform="rotate(45 38 12)" />
      <ellipse cx="22" cy="28" rx="4" ry="8" transform="rotate(45 22 28)" />
      <ellipse cx="38" cy="28" rx="4" ry="8" transform="rotate(-45 38 28)" />
    </g>
    {/* sunny center */}
    <circle cx="30" cy="20" r="5.5" fill={S.gold} stroke={S.forest} strokeWidth="1.1" />
    <circle cx="28.5" cy="19" r="0.9" fill={S.forest} opacity="0.55" />
    <circle cx="31" cy="21" r="0.9" fill={S.forest} opacity="0.55" />
    <circle cx="30" cy="18" r="0.7" fill={S.forest} opacity="0.4" />
  </svg>
);

export const Coffin = ({ className = "", size = 60 }) => (
  <svg viewBox="0 0 44 70" width={size} height={size} className={className} aria-hidden="true">
    <path
      d="M14 4 L30 4 L38 22 L38 52 L30 66 L14 66 L6 52 L6 22 Z"
      fill={S.forest}
      stroke={S.midnight}
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    {/* inner border */}
    <path d="M14 8 L30 8 L34 22 L34 50 L30 62 L14 62 L10 50 L10 22 Z" fill="none" stroke={S.gold} strokeWidth="1" opacity="0.7" />
    {/* cross of flowers */}
    <path d="M22 22 L22 46 M14 34 L30 34" stroke={S.magenta} strokeWidth="2.4" strokeLinecap="round" />
    <circle cx="22" cy="22" r="2.4" fill={S.magenta} />
    <circle cx="22" cy="46" r="2.4" fill={S.magenta} />
    <circle cx="14" cy="34" r="2.4" fill={S.magenta} />
    <circle cx="30" cy="34" r="2.4" fill={S.magenta} />
  </svg>
);

export const Candelabra = ({ className = "", size = 60 }) => (
  <svg viewBox="0 0 70 80" width={size} height={size} className={className} aria-hidden="true">
    {/* base */}
    <rect x="26" y="66" width="18" height="6" fill={S.forest} stroke={S.midnight} strokeWidth="1.2" />
    <rect x="30" y="60" width="10" height="6" fill={S.forest} stroke={S.midnight} strokeWidth="1.2" />
    {/* stem */}
    <path d="M35 60 L35 30" stroke={S.forest} strokeWidth="2.2" />
    {/* arms */}
    <path d="M35 40 Q18 42, 14 30 Q14 24, 20 26" fill="none" stroke={S.forest} strokeWidth="2" />
    <path d="M35 40 Q52 42, 56 30 Q56 24, 50 26" fill="none" stroke={S.forest} strokeWidth="2" />
    {/* candles */}
    <rect x="12" y="20" width="6" height="10" fill={S.cream} stroke={S.midnight} strokeWidth="1" />
    <rect x="32" y="18" width="6" height="12" fill={S.cream} stroke={S.midnight} strokeWidth="1" />
    <rect x="52" y="20" width="6" height="10" fill={S.cream} stroke={S.midnight} strokeWidth="1" />
    {/* flames */}
    <path d="M15 20 Q13 16, 15 12 Q17 16, 15 20 Z" fill={S.gold} />
    <path d="M35 18 Q33 12, 35 8 Q37 12, 35 18 Z" fill={S.magenta} />
    <path d="M55 20 Q53 16, 55 12 Q57 16, 55 20 Z" fill={S.gold} />
  </svg>
);

export const SpiderWeb = ({ className = "", size = 80 }) => (
  <svg viewBox="0 0 80 80" width={size} height={size} className={className} aria-hidden="true">
    <g stroke={S.forest} strokeWidth="1" fill="none" opacity="0.85">
      {/* radial spokes */}
      <line x1="0" y1="0" x2="40" y2="40" />
      <line x1="40" y1="0" x2="40" y2="40" />
      <line x1="80" y1="0" x2="40" y2="40" />
      <line x1="0" y1="40" x2="40" y2="40" />
      {/* arcs */}
      <path d="M0 0 Q22 6, 40 0" />
      <path d="M40 0 Q62 6, 80 0" />
      <path d="M0 0 Q6 22, 0 40" />
      <path d="M4 4 Q30 10, 40 30" />
      <path d="M40 30 Q54 12, 76 4" />
      <path d="M12 12 Q30 20, 40 30" />
      <path d="M40 30 Q52 20, 68 12" />
      <path d="M0 40 Q10 30, 40 40" />
    </g>
    {/* tiny spider */}
    <circle cx="40" cy="40" r="3" fill={S.midnight} />
    <path d="M37 40 L32 44 M43 40 L48 44 M38 42 L34 48 M42 42 L46 48" stroke={S.midnight} strokeWidth="1" />
  </svg>
);

export const Ghost = ({ className = "", size = 60 }) => (
  <svg viewBox="0 0 60 70" width={size} height={size} className={className} aria-hidden="true">
    <path
      d="M10 30 Q10 8, 30 8 Q50 8, 50 30 L50 60 Q46 66, 42 60 Q38 66, 34 60 Q30 66, 26 60 Q22 66, 18 60 Q14 66, 10 60 Z"
      fill={S.cream}
      stroke={S.midnight}
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <circle cx="22" cy="30" r="3" fill={S.midnight} />
    <circle cx="38" cy="30" r="3" fill={S.midnight} />
    <ellipse cx="20" cy="40" rx="3" ry="1.6" fill={S.magenta} opacity="0.65" />
    <ellipse cx="40" cy="40" rx="3" ry="1.6" fill={S.magenta} opacity="0.65" />
    <path d="M26 42 Q30 46, 34 42" fill="none" stroke={S.midnight} strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

export const GothicArch = ({ className = "", size = 100 }) => (
  <svg viewBox="0 0 80 120" width={size} height={size} className={className} aria-hidden="true">
    <path
      d="M10 115 L10 60 Q10 10, 40 4 Q70 10, 70 60 L70 115 Z"
      fill="none"
      stroke={S.forest}
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M18 115 L18 62 Q18 20, 40 12 Q62 20, 62 62 L62 115 Z"
      fill="none"
      stroke={S.forest}
      strokeWidth="1"
      opacity="0.55"
    />
    <circle cx="40" cy="4" r="4" fill={S.magenta} stroke={S.midnight} strokeWidth="1" />
    <circle cx="10" cy="60" r="3" fill={S.gold} stroke={S.midnight} strokeWidth="1" />
    <circle cx="70" cy="60" r="3" fill={S.gold} stroke={S.midnight} strokeWidth="1" />
  </svg>
);
